import type { AvatarProps, AvatarSize } from './Avatar.types';
import { Box } from '../Box';
import { styled } from '@withjoy/joykit/theme';
import { anatomy } from '@shared/joykit/packages/core/common/utils/anatomy';
import type { MultiPartStyleConfig, PartsStyleFunction } from '@shared/joykit/packages/core/common/utils/styleConfig';

export const StyledRoot = styled(Box)``;
export const StyledImage = styled(Box)``;
export const StyledFallback = styled(Box)``;

export const FallbackGradientOverlay = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%);
  mix-blend-mode: overlay;
  pointer-events: none;
`;

const avatarAnatomy = anatomy('Avatar').parts('root', 'image', 'fallback');

export const AVATAR_SIZES: Readonly<Record<AvatarSize, number>> = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 80,
};

const FONT_SIZE_MAP: Readonly<Record<AvatarSize, number>> = {
  xs: 10,
  sm: 12,
  md: 16,
  lg: 22,
  xl: 32,
};

export const resolveSize = (size: AvatarProps['size']): number => {
  if (typeof size === 'number') return size;
  return AVATAR_SIZES[size ?? 'md'] ?? AVATAR_SIZES.md;
};

export const resolveGroupSpacing = (size: AvatarProps['size']): number => {
  return -Math.round(resolveSize(size) * 0.4);
};

export const resolveFontSize = (size: AvatarProps['size']): number => {
  if (typeof size === 'number') return Math.round(size * 0.4);
  return FONT_SIZE_MAP[size ?? 'md'] ?? FONT_SIZE_MAP.md;
};

const avatarBaseStyle: PartsStyleFunction<AvatarProps, typeof avatarAnatomy> = props => {
  const px = resolveSize(props.size);

  return {
    root: {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      flexShrink: 0,
      borderRadius: '50%',
      width: `${px}px`,
      height: `${px}px`,
      verticalAlign: 'middle',
    },
    image: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    fallback: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      isolation: 'isolate',
      backgroundColor: 'mono3',
      color: 'mono10',
      fontSize: `${resolveFontSize(props.size)}px`,
      lineHeight: 1,
      fontWeight: 600,
    },
  };
};

export const avatarStyles: MultiPartStyleConfig<AvatarProps, typeof avatarAnatomy> = {
  parts: avatarAnatomy.keys,
  baseStyle: avatarBaseStyle,
};
